<script lang="ts" setup>
// Cloche de notifications v2 (port de chrome/Topbar.jsx NotifBell) — badge + popover des
// 5 dernières non lues, branché sur le store réel (useNotificationsStore.getNotifications).
// Marquer comme lue = PATCH /notifications/{id} { read: true } puis re-fetch (même contrat
// que le legacy Notifications/Card.vue).
import { onClickOutside } from '@vueuse/core';
import type { UnreadNotification } from '~/interfaces/notifications/main';

const open = ref(false);
const wrapRef = ref<HTMLElement | null>(null);

// Fetch + marquage lu partagés avec le shell mobile (cf. useUnreadNotifications). Limite 5 = les
// 5 dernières non lues affichées dans le popover ; le badge affiche le total (meta.totalItems).
const { items, unreadCount, fetchNotifs, markRead: markReadRequest } = useUnreadNotifications(5);

async function markRead (notification: UnreadNotification): Promise<void> {
  const ok = await markReadRequest(notification.id);
  if (!ok) {
    useToastService().add({ severity: 'error', summary: 'Notifications', detail: 'La notification n\'a pas pu être marquée comme lue.', life: 3000 });
  }
}

onClickOutside(wrapRef, () => { open.value = false; });
function onKey (e: KeyboardEvent): void { if (e.key === 'Escape') { open.value = false; } }
onMounted(() => {
  void fetchNotifs();
  if (import.meta.client) { window.addEventListener('keydown', onKey); }
});
onUnmounted(() => { if (import.meta.client) { window.removeEventListener('keydown', onKey); } });
</script>

<template>
  <div
    ref="wrapRef"
    class="gd-notif-wrap"
  >
    <button
      type="button"
      :class="['gd-topbar-icon', { 'is-active': open }]"
      title="Notifications"
      aria-label="Notifications"
      @click="open = !open"
    >
      <DsIcon
        name="bell"
        :size="20"
        color="var(--text-2)"
      />
      <span
        v-if="unreadCount > 0"
        class="gd-topbar-badge"
      >{{ unreadCount }}</span>
    </button>

    <div
      v-if="open"
      class="gd-notif-menu"
      role="dialog"
      aria-label="Notifications non lues"
    >
      <div
        class="gd-notif-caret"
        aria-hidden="true"
      />
      <header class="gd-notif-hd">
        <h3>Notifications non lues</h3>
        <span class="gd-notif-count">{{ unreadCount }}</span>
      </header>
      <div class="gd-notif-list">
        <div
          v-if="!items.length"
          class="gd-notif-empty"
        >
          <DsIcon
            name="check"
            :size="18"
            color="var(--ok)"
          />Aucune notification non lue
        </div>
        <LayoutV2NotifItem
          v-for="n in items"
          :key="n.id"
          :notification="n"
          @read="markRead"
        />
      </div>
      <footer class="gd-notif-ft">
        <NuxtLink
          class="gd-notif-all"
          to="/notifications"
          @click="open = false"
        >
          Voir toutes les notifications<DsIcon
            name="angle-right"
            :size="12"
          />
        </NuxtLink>
      </footer>
    </div>
  </div>
</template>
